/**
 * useVoiceTrack — la piste du micro d'un participant, pour sa vignette (weekly du 03/11, W1).
 *
 * Lu au plus près de la vignette (`VoiceSlotContext` du design system), jamais dans l'état de la salle :
 * ce crochet ne se relit qu'à l'arrivée, au départ, et quand un micro s'ouvre ou se coupe ; la voix, elle,
 * se lit sur le participant (`speakingBars`), dans le seul élément qui la dessine (`LiveVoice`).
 */
import { useMaybeRoomContext } from "@livekit/components-react"
import {
    ConnectionState,
    type LocalAudioTrack,
    type Participant,
    type RemoteAudioTrack,
    RoomEvent,
    Track,
    type TrackPublication,
} from "livekit-client"
import { useEffect, useState } from "react"

import { voiceOf } from "./voiceTrack"

export type VoiceTrack = {
    participant: Participant
    track: LocalAudioTrack | RemoteAudioTrack | undefined
}

const VOICE_EVENTS = [
    RoomEvent.ParticipantConnected,
    RoomEvent.ParticipantDisconnected,
    RoomEvent.TrackSubscribed,
    RoomEvent.TrackUnsubscribed,
    RoomEvent.TrackMuted,
    RoomEvent.TrackUnmuted,
    RoomEvent.LocalTrackPublished,
    RoomEvent.LocalTrackUnpublished,
    RoomEvent.ConnectionStateChanged,
] as const

export function useVoiceTrack(personId: string): VoiceTrack | null {
    // Hors d'une salle (une vignette rendue avant la séance, un test), pas de voix plutôt qu'une erreur.
    const room = useMaybeRoomContext()
    const [voice, setVoice] = useState<VoiceTrack | null>(null)

    useEffect(() => {
        if (room == null) return undefined
        const read = (subject?: unknown) => {
            // Une caméra ou un écran partagé qui s'ouvre ou se coupe ne change rien à la voix.
            const source = (subject as TrackPublication | undefined)?.source
            if (source != null && source !== Track.Source.Microphone) return
            // Salle coupée : pas de voix. Le SDK ne remet pas « parle » à faux à la coupure, et les barres
            // onduleraient pendant toute la reconnexion.
            const connected = room.state === ConnectionState.Connected
            const people: Participant[] = connected ? [room.localParticipant, ...room.remoteParticipants.values()] : []
            const participant = voiceOf(
                people.map((person) => ({
                    participant: person,
                    identity: person.identity,
                    micOn: person.isMicrophoneEnabled,
                })),
                personId
            )
            const track = participant?.getTrackPublication(Track.Source.Microphone)?.track as
                LocalAudioTrack | RemoteAudioTrack | undefined
            setVoice((previous) =>
                previous?.participant === participant && previous?.track === track
                    ? previous
                    : participant == null
                      ? null
                      : { participant, track }
            )
        }
        read()
        VOICE_EVENTS.forEach((event) => room.on(event, read))
        return () => {
            VOICE_EVENTS.forEach((event) => room.off(event, read))
        }
    }, [room, personId])

    return voice
}
